import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function VinylWindows() {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [lightboxOpen]);

  const styles = [
    { name: t('vinylWindows.style1'), desc: t('vinylWindows.style1Desc'), icon: 'ri-window-line' },
    { name: t('vinylWindows.style2'), desc: t('vinylWindows.style2Desc'), icon: 'ri-door-open-line' },
    { name: t('vinylWindows.style3'), desc: t('vinylWindows.style3Desc'), icon: 'ri-layout-row-line' },
    { name: t('vinylWindows.style4'), desc: t('vinylWindows.style4Desc'), icon: 'ri-picture-in-picture-line' },
    { name: t('vinylWindows.style5'), desc: t('vinylWindows.style5Desc'), icon: 'ri-layout-grid-line' },
    { name: t('vinylWindows.style6'), desc: t('vinylWindows.style6Desc'), icon: 'ri-arrow-up-line' }
  ];

  const benefits = [
    { title: t('vinylWindows.benefit1'), desc: t('vinylWindows.benefit1Desc'), icon: 'ri-leaf-line' },
    { title: t('vinylWindows.benefit2'), desc: t('vinylWindows.benefit2Desc'), icon: 'ri-volume-mute-line' },
    { title: t('vinylWindows.benefit3'), desc: t('vinylWindows.benefit3Desc'), icon: 'ri-sun-line' },
    { title: t('vinylWindows.benefit4'), desc: t('vinylWindows.benefit4Desc'), icon: 'ri-hand-coin-line' },
    { title: t('vinylWindows.benefit5'), desc: t('vinylWindows.benefit5Desc'), icon: 'ri-palette-line' },
    { title: t('vinylWindows.benefit6'), desc: t('vinylWindows.benefit6Desc'), icon: 'ri-recycle-line' }
  ];

  const glassOptions = [
    { name: t('vinylWindows.glass1'), desc: t('vinylWindows.glass1Desc') },
    { name: t('vinylWindows.glass2'), desc: t('vinylWindows.glass2Desc') },
    { name: t('vinylWindows.glass3'), desc: t('vinylWindows.glass3Desc') },
    { name: t('vinylWindows.glass4'), desc: t('vinylWindows.glass4Desc') }
  ];

  const galleryImages = [
    {
      url: 'https://public.readdy.ai/ai/img_res/99d484d68765247fe616df57fdbc36f3.jpg',
      alt: 'Double Hung Vinyl Window'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Beautiful%20white%20vinyl%20casement%20window%20in%20contemporary%20kitchen%20with%20soft%20morning%20light%20illuminating%20clean%20countertops%2C%20peaceful%20modern%20home%20interior%20with%20natural%20materials%20and%20warm%20ambient%20glow%2C%20serene%20residential%20space%20featuring%20energy%20efficient%20Milgard%20vinyl%20window%20with%20gentle%20shadows%20and%20inviting%20atmosphere&width=800&height=600&seq=vinyl-gallery-002&orientation=landscape',
      alt: 'Casement Vinyl Window'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Elegant%20white%20vinyl%20sliding%20window%20in%20cozy%20living%20room%20with%20soft%20natural%20daylight%20and%20comfortable%20furniture%2C%20tranquil%20modern%20home%20interior%20with%20warm%20earth%20tones%20and%20delicate%20ambient%20lighting%2C%20peaceful%20residential%20space%20showcasing%20premium%20Milgard%20vinyl%20window%20quality%20with%20gentle%20shadows%20and%20inviting%20atmosphere&width=800&height=600&seq=vinyl-gallery-003&orientation=landscape',
      alt: 'Sliding Vinyl Window'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Sophisticated%20white%20vinyl%20picture%20window%20in%20bright%20home%20office%20with%20soft%20natural%20light%20and%20minimalist%20workspace%2C%20serene%20contemporary%20interior%20with%20clean%20lines%20and%20warm%20ambient%20glow%2C%20peaceful%20residential%20environment%20featuring%20high%20quality%20Milgard%20vinyl%20window%20with%20delicate%20shadows%20and%20comfortable%20atmosphere&width=800&height=600&seq=vinyl-gallery-004&orientation=landscape',
      alt: 'Picture Vinyl Window'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Charming%20white%20vinyl%20bay%20window%20in%20elegant%20dining%20room%20with%20soft%20daylight%20and%20refined%20furniture%2C%20tranquil%20modern%20home%20interior%20with%20warm%20neutral%20tones%20and%20gentle%20ambient%20lighting%2C%20peaceful%20residential%20space%20showcasing%20premium%20Milgard%20vinyl%20window%20craftsmanship%20with%20delicate%20shadows%20and%20inviting%20atmosphere&width=800&height=600&seq=vinyl-gallery-005&orientation=landscape',
      alt: 'Bay Vinyl Window'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Stylish%20white%20vinyl%20awning%20window%20in%20contemporary%20bathroom%20with%20soft%20natural%20light%20and%20spa-like%20ambiance%2C%20serene%20modern%20home%20interior%20with%20clean%20surfaces%20and%20warm%20gentle%20glow%2C%20peaceful%20residential%20space%20featuring%20energy%20efficient%20Milgard%20vinyl%20window%20with%20delicate%20shadows%20and%20comfortable%20atmosphere&width=800&height=600&seq=vinyl-gallery-006&orientation=landscape',
      alt: 'Awning Vinyl Window'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Premium%20white%20vinyl%20garden%20window%20in%20bright%20kitchen%20with%20soft%20morning%20sunlight%20and%20fresh%20herbs%2C%20tranquil%20modern%20home%20interior%20with%20natural%20materials%20and%20warm%20ambient%20lighting%2C%20peaceful%20residential%20space%20showcasing%20high%20quality%20Milgard%20vinyl%20window%20with%20delicate%20shadows%20and%20inviting%20atmosphere&width=800&height=600&seq=vinyl-gallery-007&orientation=landscape',
      alt: 'Garden Vinyl Window'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Beautiful%20white%20vinyl%20horizontal%20slider%20window%20in%20cozy%20bedroom%20with%20soft%20natural%20daylight%20and%20comfortable%20bedding%2C%20serene%20contemporary%20interior%20with%20warm%20pastel%20tones%20and%20gentle%20ambient%20glow%2C%20peaceful%20residential%20environment%20featuring%20premium%20Milgard%20vinyl%20window%20quality%20with%20delicate%20shadows%20and%20inviting%20atmosphere&width=800&height=600&seq=vinyl-gallery-008&orientation=landscape',
      alt: 'Horizontal Slider Vinyl Window'
    }
  ];

  const openLightbox = (index: number) => {
    setCurrentImage(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={true} />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <img 
          src="https://readdy.ai/api/search-image?query=Beautiful%20modern%20home%20exterior%20featuring%20elegant%20white%20vinyl%20windows%20with%20clean%20lines%20and%20contemporary%20design%2C%20soft%20morning%20sunlight%20illuminating%20pristine%20window%20frames%2C%20peaceful%20residential%20neighborhood%20setting%20with%20lush%20green%20landscaping%2C%20warm%20inviting%20atmosphere%20showcasing%20energy%20efficient%20premium%20quality%20vinyl%20windows%2C%20professional%20architectural%20photography%20with%20natural%20lighting%20and%20serene%20ambiance&width=1920&height=1080&seq=vinyl-hero-901&orientation=landscape"
          alt="Milgard Vinyl Windows"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/60 via-blue-800/50 to-blue-900/70"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <p className="text-blue-200 text-xs font-semibold tracking-[0.2em] mb-4 uppercase">{t('vinylWindows.badge')}</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6 leading-tight">
            {t('vinylWindows.heroTitle')}
          </h1>
          <p className="text-blue-100 text-base md:text-lg mb-10 font-light max-w-2xl mx-auto">
            {t('vinylWindows.heroSubtitle')}
          </p>
          <a 
            href="/#contact"
            className="inline-flex items-center gap-3 bg-white text-blue-900 px-10 py-4 rounded-full text-sm font-medium hover:bg-blue-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('vinylWindows.ctaButton')}
            <i className="ri-arrow-right-line text-base"></i>
          </a>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-6">
              {t('vinylWindows.overviewTitle')}
            </h2>
            <div className="w-12 h-px bg-blue-500 mx-auto mb-6"></div>
            <p className="text-stone-600 text-sm leading-relaxed">
              {t('vinylWindows.overviewDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: t('vinylWindows.feature1'), desc: t('vinylWindows.feature1Desc'), icon: 'ri-flashlight-line' },
              { title: t('vinylWindows.feature2'), desc: t('vinylWindows.feature2Desc'), icon: 'ri-brush-line' },
              { title: t('vinylWindows.feature3'), desc: t('vinylWindows.feature3Desc'), icon: 'ri-shield-check-line' },
              { title: t('vinylWindows.feature4'), desc: t('vinylWindows.feature4Desc'), icon: 'ri-award-line' }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
                  <i className={`${feature.icon} text-blue-600 text-2xl`}></i>
                </div>
                <h3 className="text-base font-medium text-stone-800 mb-2">{feature.title}</h3>
                <p className="text-stone-600 text-xs leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Styles Section */}
      <section ref={sectionRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-4">
              {t('vinylWindows.stylesTitle')}
            </h2>
            <div className="w-12 h-px bg-blue-500 mx-auto mb-6"></div>
            <p className="text-stone-600 text-sm max-w-2xl mx-auto">
              {t('vinylWindows.stylesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {styles.map((style, index) => (
              <div 
                key={index}
                className={`group bg-stone-50 rounded-xl p-6 hover:bg-blue-50 transition-all duration-300 cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 flex items-center justify-center bg-white rounded-lg mb-4 group-hover:bg-blue-100 transition-colors">
                  <i className={`${style.icon} text-blue-600 text-2xl`}></i>
                </div>
                <h3 className="text-base font-medium text-stone-800 mb-2">{style.name}</h3>
                <p className="text-stone-600 text-xs leading-relaxed">{style.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-4">
              {t('vinylWindows.galleryTitle')}
            </h2>
            <div className="w-12 h-px bg-blue-500 mx-auto mb-6"></div>
            <p className="text-stone-600 text-sm max-w-2xl mx-auto">
              {t('vinylWindows.gallerySubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div 
                key={index}
                onClick={() => openLightbox(index)}
                className="group relative overflow-hidden rounded-lg cursor-pointer bg-stone-200"
                style={{ width: '100%', height: '240px' }}
              >
                <img 
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100 transition-all duration-300">
                    <i className="ri-search-line text-blue-600 text-xl"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer whitespace-nowrap z-50"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-2xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-line text-2xl"></i>
          </button>

          <div 
            className="max-w-6xl max-h-[90vh] px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={galleryImages[currentImage].url}
              alt={galleryImages[currentImage].alt}
              className="w-full h-full object-contain rounded-lg"
            />
            <p className="text-white text-center mt-4 text-sm">
              {galleryImages[currentImage].alt} ({currentImage + 1} / {galleryImages.length})
            </p>
          </div>
        </div>
      )}

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-4">
              {t('vinylWindows.benefitsTitle')}
            </h2>
            <div className="w-12 h-px bg-blue-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-stone-50 rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
                  <i className={`${benefit.icon} text-blue-600 text-xl`}></i>
                </div>
                <h3 className="text-base font-medium text-stone-800 mb-2">{benefit.title}</h3>
                <p className="text-stone-600 text-xs leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Glass Options Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-4">
              {t('vinylWindows.glassTitle')}
            </h2>
            <div className="w-12 h-px bg-blue-500 mx-auto mb-6"></div>
            <p className="text-stone-600 text-sm max-w-2xl mx-auto">
              {t('vinylWindows.glassSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {glassOptions.map((option, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-stone-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-stone-800 mb-2">{option.name}</h3>
                    <p className="text-stone-600 text-xs leading-relaxed">{option.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warranty Section */}
      <section className="py-20 bg-blue-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-blue-800 rounded-full mx-auto mb-6">
            <i className="ri-shield-star-line text-blue-200 text-3xl"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            {t('vinylWindows.warrantyTitle')}
          </h2>
          <p className="text-blue-100 text-sm leading-relaxed max-w-2xl mx-auto">
            {t('vinylWindows.warrantyDesc')}
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-6">
            {t('vinylWindows.ctaTitle')}
          </h2>
          <p className="text-stone-600 text-sm mb-10 max-w-2xl mx-auto">
            {t('vinylWindows.ctaDesc')}
          </p>
          <a 
            href="/#contact"
            className="inline-flex items-center gap-3 bg-blue-600 text-white px-10 py-4 rounded-full text-sm font-medium hover:bg-blue-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('vinylWindows.ctaButton')}
            <i className="ri-arrow-right-line text-base"></i>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}